import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../../graphql/client";
import { AllOrgProductsByOrgIdQuery } from "../../../../../graphql/generated/types";
import { LinkButton } from "../../../../atoms/link-button";
import { ServiceProviderTabs } from "../../../../molecules/service-provider-tab";
import { ServiceProviderTemplate } from "../../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../routes";

export const DataTransferTopPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const history = useHistory();
  const { orgProductLinks } = useOrgProducts(tenantId);

  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        {
          to: ServiceProviderR.Home.link({}),
          title: "Home",
        },
      ]}
    >
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={tenantId} />
            <div className="content">
              {orgProductLinks.length > 0 ? (
                <ul>
                  {orgProductLinks.map(({ productCode, link, text }) => {
                    return (
                      <li key={productCode}>
                        <LinkButton
                          onClick={() => {
                            history.push(link({ tenantId }));
                          }}
                        >
                          {text}
                        </LinkButton>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>
                  「製品設定」画面から、使いたい機能の製品プランを設定してください。
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

type DataTransferOrgProductCode = "KintoneExport";

type DataTransferProductLink = {
  productCode: DataTransferOrgProductCode;
  link: (args: { tenantId: string }) => string;
  text: string;
};

type DataTransferProductLinks = Record<
  DataTransferOrgProductCode,
  DataTransferProductLink
>;

type DataTransferOrgProduct =
  AllOrgProductsByOrgIdQuery["allOrgProductsByOrgId"][number] & {
    productCode: DataTransferOrgProductCode;
  };

const useOrgProducts = (tenantId: string) => {
  const [orgProductLinks, setOrgProductLinks] = React.useState<
    DataTransferProductLink[]
  >([]);

  const dataTransferProductLinks =
    React.useMemo((): DataTransferProductLinks => {
      return {
        KintoneExport: {
          productCode: "KintoneExport",
          link: ServiceProviderR.KintoneExportSettings.link,
          text: "スマート申請の申請データを kintone へ転送する",
        },
      };
    }, []);

  React.useEffect(() => {
    const fetchOrgProduct = async () => {
      const res = await runGqlQueryV2(() =>
        gqlClient.allOrgProductsByOrgId({ orgId: tenantId }, gqlHeader("q")),
      );
      if (res.error) {
        toast.error("製品情報の取得に失敗しました。");
        return;
      }
      setOrgProductLinks(
        res
          .data!.allOrgProductsByOrgId.filter<DataTransferOrgProduct>(
            (v): v is DataTransferOrgProduct =>
              Object.keys(dataTransferProductLinks).includes(v.productCode) &&
              v.plan !== "NotSet",
          )
          .map(({ productCode }) => {
            return {
              productCode,
              link: dataTransferProductLinks[productCode].link,
              text: dataTransferProductLinks[productCode].text,
            };
          }),
      );
    };
    fetchOrgProduct();
  }, [tenantId, setOrgProductLinks, dataTransferProductLinks]);

  return {
    orgProductLinks,
  };
};
